// One thing that needs somebody: an andon, a starved station, a quality alert,
// a to-do. The tag says what kind, the title says what, the meta line says
// where and how late, and the buttons on the right are the verbs — go there,
// acknowledge, close — so a leader reading the list can act from it rather
// than opening each row to find out whether they can.

import type { CSSProperties } from 'react';
import { Icon } from '@/components/ui/Icon';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';

export type NeedTagTone = 'danger' | 'warn' | 'neutral';

export interface NeedVerb {
  label: string;
  /** A tap that stays here. */
  onClick?: () => void;
  /** Or one that leaves — the Andon app owns acknowledging. */
  href?: string;
}

export interface NeedCardVM {
  key: string;
  /** "★★★", "STARVED", "QA", "TO-DO". */
  tag: string;
  tagTone: NeedTagTone;
  /** Draw the card's border in the state colour — the unanswered 3★. */
  urgent: boolean;
  title: string;
  meta: string;
  onOpen: () => void;
  /** The outline button — "Go to work centre". */
  go?: NeedVerb;
  /** The black button — "Acknowledge", "Close…", "Go to <feeder>". */
  verb?: NeedVerb;
}

const TAG_STYLE: Record<NeedTagTone, CSSProperties> = {
  danger: { background: 'var(--danger)', color: '#fff' },
  warn: { background: 'var(--warnSoft)', color: 'var(--warn)' },
  neutral: { background: 'var(--surface2)', color: 'var(--accent)' },
};

export function NeedCard({ item, compact = false }: { item: NeedCardVM; compact?: boolean }) {
  const border = item.urgent ? (item.tagTone === 'warn' ? 'var(--warn)' : 'var(--danger)') : 'var(--line)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'var(--surface)',
        border: `1px solid ${border}`,
        borderRadius: compact ? 13 : 14,
        padding: compact ? '10px 12px' : '11px 13px',
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          flex: 'none',
          borderRadius: 7,
          padding: '3px 7px',
          font: "700 10.5px 'Space Grotesk',sans-serif",
          ...TAG_STYLE[item.tagTone],
        }}
      >
        {item.tag}
      </span>
      <button
        type="button"
        className="press-row"
        onClick={item.onOpen}
        style={{ flex: 1, minWidth: 180, textAlign: 'left', whiteSpace: 'normal', minHeight: 40 }}
      >
        <span style={{ display: 'block', font: `700 ${compact ? 12.5 : 13.5}px Manrope,sans-serif`, lineHeight: 1.3 }}>
          {item.title}
        </span>
        {item.meta && (
          <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
            {item.meta}
          </span>
        )}
      </button>
      {(item.go || item.verb) && (
        <span style={{ flex: 'none', display: 'flex', gap: 6 }}>
          {item.go && <VerbButton verb={item.go} primary={false} />}
          {item.verb && <VerbButton verb={item.verb} primary />}
        </span>
      )}
    </div>
  );
}

function VerbButton({ verb, primary }: { verb: NeedVerb; primary: boolean }) {
  const style: CSSProperties = {
    minHeight: 40,
    borderRadius: 11,
    padding: primary ? '0 14px' : '0 12px',
    font: '700 12px Manrope,sans-serif',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    ...(primary
      ? { background: 'var(--ink)', color: 'var(--bg)' }
      : { background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--line)' }),
  };
  if (verb.href) {
    return (
      <a href={verb.href} target="_blank" rel="noopener" className="press" style={style}>
        {verb.label}
        <Icon name="external" size={12} />
      </a>
    );
  }
  return (
    <button type="button" className="press" onClick={verb.onClick} style={style}>
      {verb.label}
    </button>
  );
}

/** A list of cards, with the loading and empty states every list shares. */
export function NeedCardList({
  items,
  isPending = false,
  emptyText,
  compact = false,
}: {
  items: NeedCardVM[];
  isPending?: boolean;
  emptyText?: string;
  compact?: boolean;
}) {
  const t = useT();
  if (isPending && items.length === 0) {
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        <Skeleton height={62} radius={14} />
        <Skeleton height={62} radius={14} />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div style={{ background: 'var(--surface2)', borderRadius: 13 }}>
        <EmptyState style={{ padding: 16 }}>{emptyText ?? t.nothingWaitingOnYouT}</EmptyState>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8 }}>
      {items.map((item) => (
        <NeedCard key={item.key} item={item} compact={compact} />
      ))}
    </div>
  );
}
